import {
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import FixtureService from 'src/service/FixtureService'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'

// The native date/time inputs only accept "YYYY-MM-DD" / "HH:mm", so snap the stored values
// (the time column comes back as "HH:mm:ss") to that shape.
const toDateValue = (value) => {
  const m = moment(value, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss'], true)
  return m.isValid() ? m.format('YYYY-MM-DD') : ''
}
const toTimeValue = (value) => {
  const m = moment(value, ['HH:mm:ss', 'HH:mm', 'hh:mm A'], true)
  return m.isValid() ? m.format('HH:mm') : ''
}

const EditForm = ({
  fixtureId,
  visible,
  onClose,
  gameType,
  activePage,
  columnFilter,
  columnSorter,
  itemsPerPage,
  fetchData,
}) => {
  const [fixtureDetail, setFixtureDetail] = useState(null)
  const [teamList, setTeamList] = useState([])
  const [matchTypeList, setMatchTypeList] = useState([])

  const [loader, setLoader] = useState(false)

  useEffect(() => {
    if (visible && fixtureId) {
      FixtureService.getFixtureDetailOld(fixtureId).then((res) => {
        if (res.status === 200) {
          const data = res.data
          setFixtureDetail(data)

          TeamService.getTeamListByGrade(data.grade).then((teamRes) => {
            if (teamRes.status === 200) {
              setTeamList(teamRes.data || [])
              setMatchTypeList(teamRes.matchTypeList || [])
            }
          })
        }
      })
    }
  }, [visible, fixtureId])

  const validationSchema =
    gameType === 1
      ? Yup.object({ team: Yup.string().required('Team is required') })
      : Yup.object({
          team: Yup.string().required('Home team is required'),
          team2: Yup.string().required('Away team is required'),
        })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      team: fixtureDetail?.team || '',
      team2: fixtureDetail?.team2 || '',
      match_type: fixtureDetail?.match_code || '',
      vanue: fixtureDetail?.vanue || '',
      opposition_club: fixtureDetail?.opposition_club || '',
      start_date: toDateValue(fixtureDetail?.start_date),
      end_date: toDateValue(fixtureDetail?.end_date),
      start_time: toTimeValue(fixtureDetail?.start_time),
      end_time: toTimeValue(fixtureDetail?.end_time),
    },
    validationSchema,
    onSubmit: (values) => {
      setLoader(true)
      const payload = { ...values, fixtureId: fixtureDetail.id }

      FixtureService.editFixture(payload)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
            onClose()
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const handleDateTimeChange = (field) => (event) => {
    formik.setFieldTouched(field, true)
    formik.setFieldValue(field, event.target.value)
  }

  const handleStartDate = (event) => {
    const value = event.target.value
    handleDateTimeChange('start_date')(event)

    // Keep an end date that still sits on or after the new start; only drop it when it doesn't.
    const endDate = formik.values.end_date
    if (endDate && value && moment(endDate).isBefore(moment(value), 'day')) {
      formik.setFieldValue('end_date', '')
    }
  }

  return (
    <COffcanvas placement="end" visible={visible} onHide={onClose} backdrop style={{ width: 640 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Fixture</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update fixture teams, timing and match type.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          {gameType === 1 ? (
            <CCol xs={6}>
              <CFormLabel className="fw-bold">Team *</CFormLabel>
              <CFormSelect
                name="team"
                value={formik.values.team}
                onChange={formik.handleChange}
                className={formik.touched.team && formik.errors.team ? 'is-invalid' : ''}
              >
                <option value="">Select Team</option>
                {teamList.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </CFormSelect>
              <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
            </CCol>
          ) : (
            <>
              <CCol xs={6}>
                <CFormLabel className="fw-bold">Home Team *</CFormLabel>
                <CFormSelect
                  name="team"
                  value={formik.values.team}
                  onChange={formik.handleChange}
                  className={formik.touched.team && formik.errors.team ? 'is-invalid' : ''}
                >
                  <option value="">Select Home Team</option>
                  {teamList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </CFormSelect>
                <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
              </CCol>
              <CCol xs={6}>
                <CFormLabel className="fw-bold">Away Team *</CFormLabel>
                <CFormSelect
                  name="team2"
                  value={formik.values.team2}
                  onChange={formik.handleChange}
                  className={formik.touched.team2 && formik.errors.team2 ? 'is-invalid' : ''}
                >
                  <option value="">Select Away Team</option>
                  {teamList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </CFormSelect>
                <CFormFeedback invalid>{formik.errors.team2}</CFormFeedback>
              </CCol>
            </>
          )}

          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="match_type">
              Match Type *
            </CFormLabel>
            <CFormSelect
              aria-label="Select Match Type"
              name="match_type"
              id="match_type"
              value={formik.values.match_type}
              disabled
            >
              <option value={0}>Select Match Type</option>
              {matchTypeList &&
                matchTypeList.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="start_date">
              Start Date *
            </CFormLabel>
            <CFormInput
              type="date"
              id="start_date"
              name="start_date"
              value={formik.values.start_date}
              onChange={handleStartDate}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="end_date">
              End Date *
            </CFormLabel>
            <CFormInput
              type="date"
              id="end_date"
              name="end_date"
              value={formik.values.end_date}
              min={formik.values.start_date || undefined}
              onChange={handleDateTimeChange('end_date')}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="start_time">
              Start Time *
            </CFormLabel>
            <CFormInput
              type="time"
              id="start_time"
              name="start_time"
              value={formik.values.start_time}
              onChange={handleDateTimeChange('start_time')}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="end_time">
              End Time *
            </CFormLabel>
            <CFormInput
              type="time"
              id="end_time"
              name="end_time"
              value={formik.values.end_time}
              onChange={handleDateTimeChange('end_time')}
            />
          </CCol>
        </CForm>
        {fixtureDetail?.status === 2 && (
          <div className="text-medium-emphasis small mt-3">
            This fixture is completed and can no longer be edited.
          </div>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        {fixtureDetail?.status !== 2 && (
          <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
            Save Changes
          </CLoadingButton>
        )}
      </div>
    </COffcanvas>
  )
}

export default EditForm
